/**
 * Shared display helpers for the UFC consumer product.
 *
 * Contract rule: null/undefined metrics mean "insufficient sample" or
 * "unavailable" — they must render as an em dash, never as zero.
 */

export const DASH = '—';

const LIFECYCLES = [
  'SCHEDULED', 'IN_PROGRESS', 'FINAL', 'POSTPONED', 'CANCELED',
  'DELAYED', 'SUSPENDED', 'ABANDONED', 'UNKNOWN',
];

const LIFECYCLE_LABELS = {
  SCHEDULED: 'Scheduled',
  IN_PROGRESS: 'Live',
  FINAL: 'Final',
  POSTPONED: 'Postponed',
  CANCELED: 'Canceled',
  DELAYED: 'Delayed',
  SUSPENDED: 'Suspended',
  ABANDONED: 'Abandoned',
  UNKNOWN: 'TBD',
};

const PRODUCT_STATE_LABELS = {
  UPCOMING: 'Upcoming',
  TODAY: 'Fight Day',
  LIVE_WINDOW: 'Live',
  RESULT_PENDING: 'Results Pending',
  FINAL: 'Final',
  POSTPONED: 'Postponed',
  CANCELED: 'Canceled',
  DELAYED: 'Delayed',
  SUSPENDED: 'Suspended',
  ABANDONED: 'Abandoned',
  UNKNOWN: 'TBD',
};

export function normalizeLifecycle(value) {
  const v = String(value || '').toUpperCase();
  return LIFECYCLES.includes(v) ? v : 'UNKNOWN';
}

export function lifecycleLabel(value) {
  return LIFECYCLE_LABELS[normalizeLifecycle(value)];
}

export function productStateLabel(value) {
  const v = String(value || '').toUpperCase();
  return PRODUCT_STATE_LABELS[v] || PRODUCT_STATE_LABELS.UNKNOWN;
}

/** CSS modifier grouping for lifecycle badges. */
export function lifecycleTone(value) {
  const v = normalizeLifecycle(value);
  if (v === 'IN_PROGRESS') return 'live';
  if (v === 'FINAL') return 'final';
  if (v === 'SCHEDULED') return 'scheduled';
  if (v === 'UNKNOWN') return 'unknown';
  return 'disrupted'; // postponed / canceled / delayed / suspended / abandoned
}

export function productStateTone(value) {
  const v = String(value || '').toUpperCase();
  if (v === 'LIVE_WINDOW') return 'live';
  if (v === 'FINAL' || v === 'RESULT_PENDING') return 'final';
  if (v === 'UPCOMING' || v === 'TODAY') return 'scheduled';
  if (v === 'UNKNOWN' || !PRODUCT_STATE_LABELS[v]) return 'unknown';
  return 'disrupted';
}

export function isLiveLifecycle(value) {
  return normalizeLifecycle(value) === 'IN_PROGRESS';
}

export function isFinalLifecycle(value) {
  return normalizeLifecycle(value) === 'FINAL';
}

// ── Numbers ────────────────────────────────────────────────────────────────

export function fmtNum(value, digits = 1) {
  if (value == null || Number.isNaN(Number(value))) return DASH;
  return Number(value).toFixed(digits);
}

export function fmtInt(value) {
  if (value == null || Number.isNaN(Number(value))) return DASH;
  return String(Math.round(Number(value)));
}

/** Fraction (0–1) → "62%". */
export function fmtPct(value, digits = 0) {
  if (value == null || Number.isNaN(Number(value))) return DASH;
  return `${(Number(value) * 100).toFixed(digits)}%`;
}

/** 71 → 5'11" */
export function fmtHeight(inches) {
  if (inches == null || Number.isNaN(Number(inches))) return DASH;
  const total = Math.round(Number(inches));
  return `${Math.floor(total / 12)}'${total % 12}"`;
}

export function fmtInches(inches) {
  if (inches == null || Number.isNaN(Number(inches))) return DASH;
  return `${Math.round(Number(inches))}"`;
}

/** Seconds → "M:SS" clock (fights max 25 minutes). */
export function fmtClock(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return DASH;
  const total = Math.max(0, Math.round(Number(seconds)));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Minutes (decimal) → "12:24". */
export function fmtMinutes(minutes) {
  if (minutes == null || Number.isNaN(Number(minutes))) return DASH;
  return fmtClock(Number(minutes) * 60);
}

// ── Dates & times ──────────────────────────────────────────────────────────

/** ISO UTC → "Sat, Apr 13" in the viewer's timezone. */
export function fmtLocalDate(utc) {
  if (!utc) return DASH;
  try {
    return new Date(utc).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  } catch {
    return DASH;
  }
}

/** ISO UTC → "Sat, Apr 13, 2026". */
export function fmtLocalDateFull(utc) {
  if (!utc) return DASH;
  try {
    return new Date(utc).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return DASH;
  }
}

/** ISO UTC → "6:00 PM EDT" in the viewer's timezone. */
export function fmtLocalTime(utc) {
  if (!utc) return DASH;
  try {
    return new Date(utc).toLocaleTimeString('en-US', {
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    });
  } catch {
    return DASH;
  }
}

export function fmtLocalDateTime(utc) {
  if (!utc) return DASH;
  return `${fmtLocalDate(utc)} · ${fmtLocalTime(utc)}`;
}

/**
 * "YYYY-MM-DD" (already the ET event-day convention) → "Apr 13, 2026".
 * Parsed by parts so the browser timezone never shifts the date.
 */
export function fmtEtDate(dateEt) {
  if (!dateEt || !/^\d{4}-\d{2}-\d{2}$/.test(dateEt)) return DASH;
  const [y, m, d] = dateEt.split('-').map(Number);
  try {
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return DASH;
  }
}

/** Countdown to a UTC instant: {days, hours, minutes} or null once passed. */
export function countdownTo(utc) {
  if (!utc) return null;
  const target = new Date(utc).getTime();
  if (Number.isNaN(target)) return null;
  const diff = target - Date.now();
  if (diff <= 0) return null;
  const minutes = Math.floor(diff / 60000);
  return {
    days: Math.floor(minutes / 1440),
    hours: Math.floor((minutes % 1440) / 60),
    minutes: minutes % 60,
  };
}

export function countdownLabel(utc) {
  const c = countdownTo(utc);
  if (!c) return null;
  if (c.days > 0) return `${c.days}d ${c.hours}h ${c.minutes}m`;
  if (c.hours > 0) return `${c.hours}h ${c.minutes}m`;
  return `${c.minutes}m`;
}

// ── Venue / event display ──────────────────────────────────────────────────

export function venueLine(event) {
  if (!event) return null;
  const place = [event.venueCity, event.venueState || event.venueCountry]
    .filter(Boolean)
    .join(', ');
  return [event.venueName, place].filter(Boolean).join(' · ') || null;
}

// ── Insights / analytics labels ────────────────────────────────────────────

export function windowLabel(window) {
  const v = String(window || '').toLowerCase();
  if (v === 'career') return 'Career';
  if (v === 'last5') return 'Last 5';
  if (v === 'last3') return 'Last 3';
  if (v === 'fight') return 'This Fight';
  return window || DASH;
}

// ── Prediction display (existing mma-v2 model output; display only) ───────

export function pickStatLabel(pick) {
  if (!pick) return DASH;
  const type = String(pick.statType || '');
  if (type === 'fight_distance') return 'Fight Distance';
  const m = type.match(/^rounds_under_([\d.]+)$/);
  if (m) return 'Total Rounds';
  return type.replace(/_/g, ' ') || DASH;
}

export function pickActionLabel(pick) {
  if (!pick) return DASH;
  const action = String(pick.recommendedAction || '').toUpperCase();
  if (action !== 'UNDER') return action || DASH;
  if (pick.statType === 'fight_distance') return 'UNDER — does not go the distance';
  if (pick.propLine != null) return `UNDER ${pick.propLine}`;
  const m = String(pick.statType || '').match(/^rounds_under_([\d.]+)$/);
  return m ? `UNDER ${m[1]} rounds` : 'UNDER';
}

export function confidenceTierLabel(tier) {
  const v = String(tier || '').toUpperCase();
  if (v === 'HIGH') return 'High Confidence';
  if (v === 'STANDARD') return 'Standard';
  if (v === 'LEAN') return 'Lean';
  if (v === 'NO_PICK') return 'No Pick';
  return tier || DASH;
}
